// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Manages node selection state for pipeline canvas.
 *
 * Exports a single selectNode handler used for both click and double-click.
 * PipelineCanvas wraps double-click with additional config panel logic.
 */
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Node } from '@xyflow/react'
import type { PipelineNodeData } from '../../../core/contracts'

export const usePipelineSelection = (nodes: Node<PipelineNodeData>[]) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedNodeId = searchParams.get('pnode')

  const setSelectedNodeId = useCallback((nodeId: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (nodeId) {
        next.set('pnode', nodeId)
      } else {
        next.delete('pnode')
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  const selectedNode = useMemo(
    () => nodes.find(node => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  )

  const selectNode = useCallback((_event: React.MouseEvent, node: Node<PipelineNodeData>) => {
    setSelectedNodeId(node.id)
  }, [setSelectedNodeId])

  return { selectedNodeId, setSelectedNodeId, selectedNode, selectNode }
}
